import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Input from "../../../ui/common/Input";
import TextArea from "../../../ui/common/TextArea";
import ToggleSwitch from "../../../ui/toggleSwitch/ToggleSwitch";
import Button from "../../../ui/common/Button";
import { EditIcon, DeleteIcon, PlusIcon, SaveIcon, CloseIcon, SettingsIcon } from "../../../../icons/Icons";
import CustomEndpointModal from "./CustomEndpointModal";
import "./AiModal.scss";

interface CreditDeal {
  id: string;
  startDate: string;
  credits: number;
}

interface CustomEndpoint {
  serviceName: string;
  endpoint: string;
  apiKey?: string;
  bearerToken?: string;
  payloadMapping?: string;
}

/**
 * AiModal Component for managing AI settings
 * Handles client settings, goals, northstars, publication times, and credit deals
 */
const AiModal = ({ data, brandName, onSave, onSaveCredits, onDeleteCredits, onClose }: any) => {
  const [clientId, setClientId] = useState(data.clientId.value || data.clientId.default);
  
  // Fix: Ensure goals and northstars are always arrays and trim whitespace
  const [goals, setGoals] = useState(() => {
    const goalsValue = data.goals.value;
    const goalsDefault = data.goals.default;
    
    if (goalsValue) {
      return typeof goalsValue === 'string' 
        ? goalsValue.split(',').map(goal => goal.trim())
        : goalsValue;
    } else if (goalsDefault) {
      return typeof goalsDefault === 'string' 
        ? goalsDefault.split(',').map(goal => goal.trim())
        : goalsDefault;
    } else {
      return [];
    }
  });
  
  const [northstars, setNorthstars] = useState(() => {
    const northstarsValue = data.northstars.value;
    const northstarsDefault = data.northstars.default;
    
    if (northstarsValue) {
      return typeof northstarsValue === 'string' 
        ? northstarsValue.split(',').map(item => item.trim())
        : northstarsValue;
    } else if (northstarsDefault) {
      return typeof northstarsDefault === 'string' 
        ? northstarsDefault.split(',').map(item => item.trim())
        : northstarsDefault;
    } else {
      return [];
    }
  });
  
  const [publicationTimeFrom, setPublicationTimeFrom] = useState(
    data.publicationTimeFrom.value || data.publicationTimeFrom.default,
  );
  const [publicationTimeTo, setPublicationTimeTo] = useState(
    data.publicationTimeTo.value || data.publicationTimeTo.default,
  );
  
  // Added mainContent and excludeContent fields
  const [mainContent, setMainContent] = useState(data.mainContent.value || data.mainContent.default || "");
  const [excludeContent, setExcludeContent] = useState(data.excludeContent.value || data.excludeContent.default || "");
  
  const [customEndpoints, setCustomEndpoints] = useState<CustomEndpoint[]>(
    data.customEndpoints?.value || data.customEndpoints?.default || []
  );
  const [endpointModalOpen, setEndpointModalOpen] = useState(false);
  const [selectedGoalForEndpoint, setSelectedGoalForEndpoint] = useState<string | null>(null);

  const updatingCredits = useSelector((state: any) => state.credits.updatingCredits);
  const updateCreditsError = useSelector((state: any) => state.credits.creatingBrandError);

  const emptyCreditDeal = {
    id: "",
    startDate: new Date().toISOString().split("T")[0],
    credits: 0,
  };

  const [creditDealEditIndex, setCreditDealEditIndex] = useState(null);
  const [creditDealEditData, setCreditDealEditData] = useState<CreditDeal>(emptyCreditDeal);

  /* Credit deals 
  [{
      "credits" : 1000,
      "id" : "deal1",
      "startDate" : "2023-12-13"
  }]
  */
  const [creditDeals, setCreditDeals] = useState(data.creditDeals.value || data.creditDeals.default);

  const allGoals = useMemo(() => {
    return ["headliner", "paragraph", "metadata", "readability", "sentiment", "timeslots", "userneeds"];
  }, []);
  
  const defaultGoals = useMemo(() => {
    return ["headliner", "paragraph"];
  }, []);

  const allNorthstars = useMemo(() => {
    return ["attentiontime", "conversions", "pageviews", "social"];
  }, []);

  /**
   * Handles toggling goals on/off
   * Ensures no duplicates by checking normalized values
   */
  const handleGoalSwitch = useCallback(
    (clickedGoal) => {
      // Remove any existing instances of this goal (with or without spaces)
      const cleanedGoals = goals.filter((goal) => goal.trim() !== clickedGoal.trim());
      
      if (goals.some(goal => goal.trim() === clickedGoal.trim())) {
        // Goal exists (after trimming), just remove it
        setGoals(cleanedGoals);
      } else {
        // Goal doesn't exist, add it (trimmed)
        setGoals([...cleanedGoals, clickedGoal.trim()]);
      }
    },
    [goals],
  );

  /**
   * Updates credit deal form fields
   */
  const handleInputChange = (e, field) => {
    setCreditDealEditData({
      ...creditDealEditData,
      [field]: e.target.value,
    });
  };

  /**
   * Handles toggling northstars on/off
   * Ensures no duplicates by checking normalized values
   */
  const handleNorthstarSwitch = useCallback(
    (clickedNorthstar) => {
      // Remove any existing instances of this northstar (with or without spaces)
      const cleanedNorthstars = northstars.filter((northstar) => 
        northstar.trim() !== clickedNorthstar.trim()
      );
      
      if (northstars.some(northstar => northstar.trim() === clickedNorthstar.trim())) {
        // Northstar exists (after trimming), just remove it
        setNorthstars(cleanedNorthstars);
      } else {
        // Northstar doesn't exist, add it (trimmed)
        setNorthstars([...cleanedNorthstars, clickedNorthstar.trim()]);
      }
    },
    [northstars],
  );

  /**
   * Saves changes to a credit deal
   */
  const handleCreditSave = () => {
    const updatedDeals = creditDeals.map((deal, key) => {
      if (key === creditDealEditIndex) {
        return {
          ...creditDealEditData,
        };
      }
      return deal;
    });

    setCreditDeals(updatedDeals);
    onSaveCredits(creditDealEditData);
    setCreditDealEditIndex(null);
  };
  
  /**
   * Cancels editing of a credit deal
   */
  const handleCreditCancel = () => {
    // Check if the id of the credit deal is empty, if so remove it from the list
    if (creditDealEditData.id === "") {
      const updatedDeals = creditDeals.filter((deal, key) => {
        return key !== creditDealEditIndex;
      });
      setCreditDeals(updatedDeals);
    }
    setCreditDealEditIndex(null);
  };

  /**
   * Deletes a credit deal
   */
  const handleCreditDelete = (key) => {
    const updatedDeals = creditDeals.filter((deal, index) => {
      return index !== key;
    });
    onDeleteCredits(creditDeals[key].id);
    setCreditDeals(updatedDeals);
  };

  /**
   * Creates a new empty credit deal
   */
  const handleCreditNew = () => {
    // add empty credit deal to the list
    const updatedDeals = [...creditDeals, emptyCreditDeal];
    setCreditDeals(updatedDeals);
    setCreditDealEditIndex(creditDeals.length);
    setCreditDealEditData(emptyCreditDeal);
  };

  /**
   * Opens the custom endpoint modal for a specific goal
   */
  const handleOpenEndpointModal = (goalName: string) => {
    setSelectedGoalForEndpoint(goalName);
    setEndpointModalOpen(true);
  };

  /**
   * Saves or updates a custom endpoint
   */
  const handleSaveCustomEndpoint = (endpoint: CustomEndpoint) => {
    setCustomEndpoints((prevEndpoints) => {
      const existingIndex = prevEndpoints.findIndex(
        (e) => e.serviceName === endpoint.serviceName
      );
      
      if (existingIndex >= 0) {
        const updated = [...prevEndpoints];
        updated[existingIndex] = endpoint;
        return updated;
      } else {
        return [...prevEndpoints, endpoint];
      }
    });
  };

  /**
   * Saves all AI settings
   * Ensures data is normalized before saving
   */
  const handleSave = () => {    
    // Clean up any potential duplicates before saving
    const uniqueGoals = [...new Set(goals.map(g => g.trim()))];
    const uniqueNorthstars = [...new Set(northstars.map(n => n.trim()))];
    
    const dataToSave = {
      clientId: {
        default: data.clientId.default,
        value: clientId,
      },
      goals: {
        default: data.goals.default,
        value: uniqueGoals.join(","),
      },
      northstars: {
        default: data.northstars.default,
        value: uniqueNorthstars.join(","),
      },
      publicationTimeFrom: {
        default: data.publicationTimeFrom.default,
        value: publicationTimeFrom,
      },
      publicationTimeTo: {
        default: data.publicationTimeTo.default,
        value: publicationTimeTo,
      },
      // Include mainContent and excludeContent in the saved data
      mainContent: {
        default: data.mainContent.default, // Fixed typo: was 'defaul'
        value: mainContent,
      },
      excludeContent: {
        default: data.excludeContent.default,
        value: excludeContent,
      },
      customEndpoints: {
        default: data.customEndpoints?.default || [],
        value: customEndpoints,
      }
    };

    onSave(dataToSave);
  };

  return (
    <div className="ai-modal-wrapper">
      <h3>AI settings for {brandName}</h3>
      <div className="columns-container">
        <div className="column-left">
          <Input
            label="Client Id"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            separateLines
          />
          
          {/* Added mainContent and excludeContent inputs */}
          <Input
            label="Main Content"
            value={mainContent}
            onChange={(e) => setMainContent(e.target.value)}
            separateLines
            fullWidth
          />
          
          <Input
            label="Exclude Content"
            value={excludeContent}
            onChange={(e) => setExcludeContent(e.target.value)}
            separateLines
            fullWidth
          />
          
          <p>Credit Deals</p>
          <div className="creadit-deals-container">
            <div className="credit-deal-header">
              <div className="starting-date-deal">
                <p>Starting deal date</p>
              </div>
              <div className="number-of-credits">
                <p>Number of credits</p>
              </div>
              <div className="number-of-credits"></div>
            </div>
            <div className="credit-deal-body">
              {creditDeals.map((deal, key) => {
                return (
                  <div className="credit-deal" key={key}>
                    <div className="credit-deal-date">
                      {creditDealEditIndex === key ? (
                        <Input
                          type="date"
                          value={creditDealEditData?.startDate}
                          onChange={(e) => handleInputChange(e, "startDate")}
                          placeholder="dd-mm-yyyy"
                          min="2000-01-01"
                          max="2030-12-31"
                          pattern="\d{4}-\d{2}-\d{2}"
                          data-date-format="YYYY-MM-DD"
                        />
                      ) : (
                        <p>{deal.startDate}</p>
                      )}
                    </div>
                    <div className="credit-deal-credits">
                      {creditDealEditIndex === key ? (
                        <Input
                          type="number"
                          value={creditDealEditData?.credits}
                          onChange={(e) => handleInputChange(e, "credits")}
                        />
                      ) : (
                        <p>{deal.credits}</p>
                      )}
                    </div>
                    <div className="credit-deal-buttons">
                      <Button.Feedback
                        variant="success"
                        data-testid={`editButton${key}`}
                        size="small"
                        title="Save"
                        onClick={() => {
                          handleCreditSave();
                        }}
                        style={{ display: creditDealEditIndex === key ? "" : "none" }}
                      >
                        <SaveIcon />
                      </Button.Feedback>

                      <Button
                        variant="warning"
                        data-testid={`editButton${key}`}
                        size="small"
                        title="Cancel"
                        onClick={() => {
                          handleCreditCancel();
                        }}
                        style={{
                          marginLeft: "10px",
                          marginRight: "10px",
                          display: creditDealEditIndex === key ? "" : "none",
                        }}
                      >
                        <CloseIcon />
                      </Button>

                      <Button
                        variant="warning"
                        data-testid={`editButton${key}`}
                        size="small"
                        title="Edit"
                        onClick={() => {
                          setCreditDealEditData(deal);
                          setCreditDealEditIndex(key);
                        }}
                        // hide if editing
                        style={{ display: creditDealEditIndex === key ? "none" : "" }}
                      >
                        <EditIcon />
                      </Button>
                      <Button
                        variant="danger"
                        data-testid={`deleteButton${key}`}
                        size="small"
                        title="Delete"
                        style={{
                          marginLeft: "10px",
                          marginRight: "10px",
                          display: creditDealEditIndex === key ? "none" : "",
                        }}
                        onClick={() => {
                          handleCreditDelete(key);
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="credit-deal-footer">
              <Button
                variant="success"
                size="small"
                title={creditDealEditIndex !== null ? "First save or cancel editing" : "Add new credit deal"}
                style={{ marginLeft: "46%", marginRight: "47%" }}
                onClick={() => {
                  handleCreditNew();
                }}
                disabled={creditDealEditIndex !== null}
              >
                <PlusIcon />
              </Button>
            </div>
          </div>
        </div>
        <div className="column-right">
          <div className="goals-northstarts-container">
            <div className="goals-container">
              <p>Goals</p>
              {allGoals.map((goal) => {
                const isDefaultGoal = defaultGoals.includes(goal);
                const isActive = isDefaultGoal || goals.includes(goal);
                return (
                  <div className="switch-wrapper" key={goal}>
                    {!isDefaultGoal && (
                      <div className="switch-container">
                        <ToggleSwitch
                          checked={goals.includes(goal)}
                          onChange={() => {
                            handleGoalSwitch(goal);
                          }}
                        />
                      </div>
                    )}
                    <div className={`switch-title ${isDefaultGoal ? 'default-goal' : ''}`}>{goal}</div>
                    {isActive && (
                      <div 
                        className="settings-icon-container" 
                        onClick={() => handleOpenEndpointModal(goal)}
                        title={`Configure custom endpoint for ${goal}`}
                      >
                        {/* <SettingsIcon width="32px" height="32px" color="#666" /> */}
                        <SettingsIcon width="30px" height="35px" color="#1abcae" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="northstart-container">
              <p>Northstars</p>
              {allNorthstars.map((northstar) => {
                return (
                  <div className="switch-wrapper" key={northstar}>
                    <div className="switch-container">
                      <ToggleSwitch
                        checked={northstars.includes(northstar)}
                        onChange={() => {
                          handleNorthstarSwitch(northstar);
                        }}
                      />
                    </div>
                    <div className="switch-title">{northstar}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <Input
            label="Publication Time From"
            value={publicationTimeFrom}
            onChange={(e) => {
              setPublicationTimeFrom(e.target.value);
            }}
            separateLines
          />
          <Input
            label="Publication Time To"
            value={publicationTimeTo}
            onChange={(e) => {
              setPublicationTimeTo(e.target.value);
            }}
            separateLines
          />
        </div>
      </div>

      <div className="sub-modal-actions">
        <Button
          variant="danger"
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={() => {
            handleSave();
            onClose();
          }}
        >
          OK
        </Button>
      </div>

      {endpointModalOpen && selectedGoalForEndpoint && (
        <CustomEndpointModal
          serviceName={selectedGoalForEndpoint}
          existingEndpoint={customEndpoints.find(e => e.serviceName === selectedGoalForEndpoint)}
          onSave={handleSaveCustomEndpoint}
          onClose={() => setEndpointModalOpen(false)}
        />
      )}
    </div>
  );
};

export default AiModal;